import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useTenant } from '@/contexts/TenantContext';
import {
  Download,
  FileSpreadsheet,
  Loader2,
  Calendar as CalendarIcon,
  Search,
  Filter,
  Users,
  Building2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ExportFilters {
  dateFrom: string;
  dateTo: string;
  searchTerm: string;
  exportType: 'leads' | 'submissions' | 'comprehensive';
}

interface ExportStats {
  totalLeads: number;
  totalSubmissions: number;
  dateRange: string;
}

interface Lead {
  id: string;
  licence_location: string;
  physical_property: string;
  client_visits: string;
  cyber_concerns: string;
  professional_advice: string;
  recommended_products: string[];
  created_at: string;
  lead_contacts: {
    name: string;
    email: string;
    phone: string;
  }[];
}

export const TenantLeadExport: React.FC = () => {
  const { tenant } = useTenant();
  const { toast } = useToast();
  
  const [filters, setFilters] = useState<ExportFilters>({
    dateFrom: '',
    dateTo: '',
    searchTerm: '',
    exportType: 'leads'
  });
  
  const [stats, setStats] = useState<ExportStats>({
    totalLeads: 0,
    totalSubmissions: 0,
    dateRange: 'All time'
  });
  
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!tenant) return;
    
    setLoading(true);
    try {
      let leadsQuery = supabase
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenant.id);

      let submissionsQuery = supabase
        .from('submissions')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenant.id);

      // Apply date filters if provided
      if (filters.dateFrom) {
        leadsQuery = leadsQuery.gte('created_at', filters.dateFrom);
        submissionsQuery = submissionsQuery.gte('created_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        leadsQuery = leadsQuery.lte('created_at', filters.dateTo + 'T23:59:59');
        submissionsQuery = submissionsQuery.lte('created_at', filters.dateTo + 'T23:59:59');
      }

      const [leadsResult, submissionsResult] = await Promise.all([
        leadsQuery,
        submissionsQuery
      ]);

      setStats({
        totalLeads: leadsResult.count || 0,
        totalSubmissions: submissionsResult.count || 0,
        dateRange: filters.dateFrom && filters.dateTo 
          ? `${filters.dateFrom} - ${filters.dateTo}`
          : 'All time'
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [tenant, filters.dateFrom, filters.dateTo]);

  // Fetch stats when filters change
  useEffect(() => {
    if (tenant) {
      fetchStats();
    }
  }, [tenant, fetchStats]);

  const exportLeads = async () => {
    if (!tenant) {
      toast({
        title: 'Error',
        description: 'No tenant selected',
        variant: 'destructive',
      });
      return;
    }

    setExporting(true);
    try {
      let query = supabase
        .from('leads')
        .select(`
          id,
          licence_location,
          physical_property,
          client_visits,
          cyber_concerns,
          professional_advice,
          recommended_products,
          created_at,
          lead_contacts (
            name,
            email,
            phone
          )
        `)
        .eq('tenant_id', tenant.id)
        .order('created_at', { ascending: false });

      // Apply date filters if provided
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo + 'T23:59:59');
      }

      const { data: leads, error } = await query;

      if (error) {
        console.error('Error fetching leads:', error);
        toast({
          title: 'Export Failed',
          description: 'Error fetching leads. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      if (!leads || leads.length === 0) {
        toast({
          title: 'No Data',
          description: 'No leads found for the selected criteria.',
          variant: 'destructive',
        });
        return;
      }

      // Convert to CSV
      const csvContent = convertToCSV(leads);
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      const dateRange = filters.dateFrom && filters.dateTo ? `_${filters.dateFrom}_to_${filters.dateTo}` : '';
      const filename = `${tenant.name.replace(/\s+/g, '_')}_leads${dateRange}_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Export Successful',
        description: `Downloaded ${leads.length} leads to ${filename}`,
      });
      
    } catch (error) {
      console.error('Error exporting leads:', error);
      toast({
        title: 'Export Failed',
        description: 'Error exporting leads. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  const convertToCSV = (leads: Lead[]): string => {
    const headers = [
      'Lead ID',
      'Contact Name',
      'Email',
      'Phone',
      'License Location',
      'Physical Property',
      'Client Visits',
      'Cyber Concerns',
      'Professional Advice',
      'Recommended Products',
      'Created Date'
    ];

    const rows = leads.map(lead => {
      const contact = lead.lead_contacts?.[0] || { name: '', email: '', phone: '' };
      return [
        lead.id,
        contact.name,
        contact.email,
        contact.phone,
        lead.licence_location,
        lead.physical_property,
        lead.client_visits,
        lead.cyber_concerns,
        lead.professional_advice,
        lead.recommended_products?.join('; ') || '',
        new Date(lead.created_at).toLocaleDateString()
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    return csvContent;
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      searchTerm: '',
      exportType: 'leads'
    });
  };

  if (!tenant) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">
            No tenant selected
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Export Leads & Data
        </CardTitle>
        <CardDescription>
          Export and download your tenant's lead data with custom filters
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Tenant Info */}
        <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
          <Building2 className="h-4 w-4" />
          <span className="font-medium">Exporting for:</span>
          <Badge variant="outline">{tenant.name}</Badge>
          <span className="text-sm text-muted-foreground">({tenant.subdomain})</span>
        </div>

        {/* Filters Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            <h3 className="font-medium">Filters</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date From */}
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
              />
            </div>

            {/* Date To */}
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
              />
            </div>

            {/* Search Term */}
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Company name, email..."
                  value={filters.searchTerm}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                  className="pl-8"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </div>

        <Separator />

        {/* Stats Preview */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <h3 className="font-medium">Export Preview</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="text-2xl font-bold">
                {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.totalLeads}
              </div>
              <div className="text-sm text-muted-foreground">Total Leads</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-2xl font-bold">
                {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.totalSubmissions}
              </div>
              <div className="text-sm text-muted-foreground">Total Submissions</div>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="text-sm font-medium">Date Range</div>
              <div className="text-sm text-muted-foreground">{stats.dateRange}</div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Export Button */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-muted-foreground">
            Export will include filtered data based on your selections above
          </div>
          <Button 
            onClick={exportLeads}
            disabled={exporting || loading}
            className="min-w-[140px]"
          >
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export to CSV
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
